import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Block, Restore, Save, Search } from '@mui/icons-material';
import { authGet, authPatch, authPost } from '../../lib/api';
import SavedColumnViewControls, { useSavedColumnView } from '../../components/SavedColumnViewControls';
import { dateOnly, formatDate, todayDate } from '../../lib/dateFormat';

function money(value) {
  const amount = Number(value || 0);
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function studentLabel(student) {
  if (!student) return '';
  const name = `${student.first_name || ''} ${student.middle_name || ''} ${student.last_name || ''}`.replace(/\s+/g, ' ').trim();
  const code = student.code ? ` • ${student.code}` : '';
  const driver = student.driver_number ? ` • DL ${student.driver_number}` : '';
  return `${name || 'Unnamed student'}${code}${driver}`;
}

const EMPTY_NEW_PAYMENT = {
  student: null,
  amount: '',
  payment_date: todayDate(),
  payment_time: '',
  method: '',
  reference_number: '',
  notes: ''
};

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [includeVoided, setIncludeVoided] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newPayment, setNewPayment] = useState(EMPTY_NEW_PAYMENT);
  const [studentQuery, setStudentQuery] = useState('');
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentLoading, setStudentLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryClassId, setSummaryClassId] = useState('');
  const [summaryDate, setSummaryDate] = useState(todayDate());
  const [error, setError] = useState('');

  const loadPaymentSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const params = new URLSearchParams();
      if (summaryClassId) params.set('scheduledClassId', summaryClassId);
      if (summaryDate) params.set('date', summaryDate);
      const data = await authGet(`/api/payments/summary?${params.toString()}`);
      const eligibleClassIds = new Set((data.classes || []).map((item) => item.id));
      if (summaryClassId && summaryClassId !== 'unassigned' && !eligibleClassIds.has(summaryClassId)) {
        setSummaryClassId('');
      } else if (summaryClassId === 'unassigned' && !data.includesUnassigned) {
        setSummaryClassId('');
      }
      setSummary(data);
    } catch (err) {
      console.error('Load payment summary error:', err);
      setSummary(null);
      setError(err.message || 'Unable to load payment summary');
    } finally {
      setSummaryLoading(false);
    }
  }, [summaryClassId, summaryDate]);

  const loadPayments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (includeVoided) params.set('includeVoided', 'true');
      const data = await authGet(`/api/payments?${params.toString()}`);
      setPayments(data.payments || []);
    } catch (err) {
      console.error('Load payments error:', err);
      setError(err.message || 'Unable to load payments');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [q, includeVoided]);

  const searchStudents = useCallback(async (query) => {
    const trimmed = String(query || '').trim();
    if (trimmed.length < 2) {
      setStudentOptions([]);
      return;
    }

    setStudentLoading(true);
    try {
      const data = await authGet(`/api/students/search/list?q=${encodeURIComponent(trimmed)}&limit=20`);
      setStudentOptions(data.students || []);
    } catch (err) {
      console.error('Student search error:', err);
      setStudentOptions([]);
    } finally {
      setStudentLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  useEffect(() => {
    loadPaymentSummary();
  }, [loadPaymentSummary]);

  useEffect(() => {
    if (!adding) return undefined;
    const handle = setTimeout(() => searchStudents(studentQuery), 250);
    return () => clearTimeout(handle);
  }, [adding, searchStudents, studentQuery]);

  const rows = useMemo(() => payments.map((payment) => ({
    ...payment,
    student_name: String(payment.student_name || '').replace(/\s+/g, ' ').trim(),
    payment_date_display: formatDate(payment.payment_date),
    amount_display: money(payment.amount)
  })), [payments]);

  const saveEdit = async () => {
    if (!editing) return;
    setError('');
    try {
      await authPatch(`/api/payments/${editing.id}`, {
        amount: editing.amount === '' ? 0 : Number(editing.amount),
        payment_date: editing.payment_date || null,
        payment_time: editing.payment_time || null,
        method: editing.method || '',
        reference_number: editing.reference_number || '',
        notes: editing.notes || '',
        is_void: Boolean(editing.is_void)
      });
      setEditing(null);
      await Promise.all([loadPayments(), loadPaymentSummary()]);
    } catch (err) {
      setError(err.message || 'Unable to save payment');
    }
  };

  const openAddDialog = () => {
    setError('');
    setNewPayment({ ...EMPTY_NEW_PAYMENT, payment_date: todayDate() });
    setStudentQuery('');
    setStudentOptions([]);
    setAdding(true);
  };

  const createPayment = async () => {
    setError('');
    if (!newPayment.student?.id) {
      setError('Select a student before adding the payment.');
      return;
    }

    try {
      await authPost('/api/payments', {
        student_id: newPayment.student.id,
        amount: newPayment.amount === '' ? 0 : Number(newPayment.amount),
        payment_date: newPayment.payment_date || null,
        payment_time: newPayment.payment_time || null,
        method: newPayment.method || '',
        reference_number: newPayment.reference_number || '',
        notes: newPayment.notes || ''
      });
      setAdding(false);
      setNewPayment(EMPTY_NEW_PAYMENT);
      await Promise.all([loadPayments(), loadPaymentSummary()]);
    } catch (err) {
      setError(err.message || 'Unable to add payment');
    }
  };

  const toggleVoid = async (payment) => {
    const voided = !payment.is_void;
    const verb = voided ? 'void' : 'restore';
    if (!window.confirm(`Are you sure you want to ${verb} this payment for ${money(payment.amount)}?`)) return;

    setError('');
    try {
      await authPatch(`/api/payments/${payment.id}/void`, { voided });
      await Promise.all([loadPayments(), loadPaymentSummary()]);
    } catch (err) {
      setError(err.message || `Unable to ${verb} payment`);
    }
  };

  const columns = [
    { field: 'payment_date_display', headerName: 'Date', width: 120 },
    { field: 'amount_display', headerName: 'Amount', width: 110 },
    { field: 'student_name', headerName: 'Student', flex: 1.2 },
    { field: 'student_code', headerName: 'Student Code', width: 120 },
    { field: 'class_name', headerName: 'Class', flex: 1.1 },
    { field: 'method', headerName: 'Method', width: 120 },
    { field: 'reference_number', headerName: 'Reference', width: 130 },
    { field: 'legacy_acl_code', headerName: 'ACL Payment #', width: 130 },
    { field: 'notes', headerName: 'Notes', flex: 1 },
    {
      field: 'is_void',
      headerName: 'Void',
      width: 80,
      valueGetter: (_, row = {}) => row.is_void ? 'Yes' : 'No'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 190,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={() => setEditing({ ...params.row })}>
            Edit
          </Button>
          <Button
            size="small"
            color={params.row.is_void ? 'success' : 'error'}
            variant="outlined"
            startIcon={params.row.is_void ? <Restore /> : <Block />}
            onClick={() => toggleVoid(params.row)}
          >
            {params.row.is_void ? 'Restore' : 'Void'}
          </Button>
        </Stack>
      )
    }
  ];

  const {
    columnVisibilityModel,
    setColumnVisibilityModel,
    setFieldVisible,
    showSimpleView,
    showFullView,
    resetView
  } = useSavedColumnView('payments', columns, ['payment_date_display', 'amount_display', 'student_name', 'student_code', 'method']);

  return (
    <Box m="20px">
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} mb={2}>
        <Box>
          <Typography variant="h4" fontWeight="700">Payments</Typography>
          <Typography variant="body1" color="text.secondary">
            View, add, edit, and void payment history.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField size="small" label="Search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadPayments()} />
          <Button variant="contained" startIcon={<Search />} onClick={loadPayments}>Search</Button>
          <Button variant="contained" color="success" startIcon={<Add />} onClick={openAddDialog}>Add Payment</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} mb={2}>
          <TextField
            size="small"
            label="Calendar Date"
            type="date"
            value={summaryDate}
            onChange={(event) => setSummaryDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': 'Payment summary calendar date' }}
          />
          <TextField
            select
            size="small"
            label="Class with payments"
            value={summaryClassId}
            onChange={(event) => setSummaryClassId(event.target.value)}
            inputProps={{ 'aria-label': 'Payment summary class filter' }}
            sx={{ minWidth: { xs: '100%', sm: 320 } }}
          >
            <MenuItem value="">All Classes</MenuItem>
            {summary?.includesUnassigned && <MenuItem value="unassigned">Unassigned</MenuItem>}
            {(summary?.classes || []).map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
          </TextField>
        </Stack>
        <Grid container spacing={2}>
          {[
            ['Today', summary?.periods?.day],
            ['This Week (Mon–Sun)', summary?.periods?.week],
            ['This Month', summary?.periods?.month]
          ].map(([label, period]) => (
            <Grid item xs={12} md={4} key={label}>
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 2, minHeight: 116 }}>
                <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
                <Typography variant="h5" fontWeight="700">{summaryLoading ? '—' : money(period?.amount)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {summaryLoading ? 'Loading…' : `${period?.count || 0} payment${Number(period?.count || 0) === 1 ? '' : 's'}`}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Checkbox checked={includeVoided} onChange={(e) => setIncludeVoided(e.target.checked)} />
          <Typography variant="body2">Include voided payments</Typography>
        </Stack>
      </Paper>

      <Paper sx={{ height: 650, p: 2 }}>
        <SavedColumnViewControls
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          setFieldVisible={setFieldVisible}
          showSimpleView={showSimpleView}
          showFullView={showFullView}
          resetView={resetView}
        />
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          getRowClassName={(params) => params.row.is_void ? 'voided-payment-row' : ''}
          sx={{
            '& .voided-payment-row': {
              opacity: 0.6,
              textDecoration: 'line-through'
            }
          }}
        />
      </Paper>

      <Dialog open={adding} onClose={() => setAdding(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Payment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12}>
              <Autocomplete
                options={studentOptions}
                loading={studentLoading}
                value={newPayment.student}
                inputValue={studentQuery}
                getOptionLabel={studentLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onInputChange={(_, value) => setStudentQuery(value)}
                onChange={(_, value) => setNewPayment({ ...newPayment, student: value })}
                renderInput={(params) => (
                  <TextField {...params} label="Search student" helperText="Type at least 2 characters, then select the student." fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}><TextField fullWidth required label="Amount" type="number" value={newPayment.amount} onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Payment Date" type="date" InputLabelProps={{ shrink: true }} value={newPayment.payment_date} onChange={(e) => setNewPayment({ ...newPayment, payment_date: e.target.value })} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Payment Time" type="time" InputLabelProps={{ shrink: true }} value={newPayment.payment_time} onChange={(e) => setNewPayment({ ...newPayment, payment_time: e.target.value })} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Method" value={newPayment.method} onChange={(e) => setNewPayment({ ...newPayment, method: e.target.value })} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Reference" value={newPayment.reference_number} onChange={(e) => setNewPayment({ ...newPayment, reference_number: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Notes" value={newPayment.notes} onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(false)}>Cancel</Button>
          <Button variant="contained" color="success" startIcon={<Add />} onClick={createPayment}>Add Payment</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Payment</DialogTitle>
        <DialogContent>
          {editing && (
            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={12} md={4}><TextField fullWidth label="Amount" type="number" value={editing.amount || ''} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Payment Date" type="date" InputLabelProps={{ shrink: true }} value={dateOnly(editing.payment_date)} onChange={(e) => setEditing({ ...editing, payment_date: e.target.value })} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Payment Time" type="time" InputLabelProps={{ shrink: true }} value={String(editing.payment_time || '').slice(0, 5)} onChange={(e) => setEditing({ ...editing, payment_time: e.target.value })} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="Method" value={editing.method || ''} onChange={(e) => setEditing({ ...editing, method: e.target.value })} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="Reference" value={editing.reference_number || ''} onChange={(e) => setEditing({ ...editing, reference_number: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Notes" value={editing.notes || ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></Grid>
              <Grid item xs={12}><Stack direction="row" alignItems="center"><Checkbox checked={Boolean(editing.is_void)} onChange={(e) => setEditing({ ...editing, is_void: e.target.checked })} /><Typography>Void this payment</Typography></Stack></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" startIcon={<Save />} onClick={saveEdit}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
